import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  Form,
  Input,
  Button,
  Select,
  Upload,
  message,
  Typography,
  Row,
  Col,
  InputNumber,
  Switch,
  Space,
  Popconfirm
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined,
  MinusCircleOutlined
} from "@ant-design/icons";
import warehouseApi from "../api/warehouseApi";
import { selectAllPackageTypes } from "modules/client/package-types/store/slice";
import { getPackageTypeList } from "modules/client/package-types/store/thunks";
import "./EditInventoryItemPage.scss";

const { Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EditInventoryItemPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(null);
  const [warehouse, setWarehouse] = useState(null);
  const [categories, setCategories] = useState([]);
  const [measurementUnit, setMeasurementUnit] = useState("in");
  const [bulkAddCount, setBulkAddCount] = useState(1);
  const { inventoryId, itemId } = useParams();
  const warehouseId = inventoryId;
  const history = useHistory();
  const dispatch = useDispatch();
  const packageTypes = useSelector(selectAllPackageTypes);

  const locations = [
    { id: "loc-1", name: "Main Warehouse" },
    { id: "loc-2", name: "Storage Room A" },
    { id: "loc-3", name: "Cold Storage" },
    { id: "loc-4", name: "Shipping Dock" }
  ];

  useEffect(() => {
    loadItem();
    loadWarehouse();
    loadCategories();
    dispatch(getPackageTypeList());
  }, [warehouseId, itemId]);

  const loadWarehouse = async () => {
    try {
      const foundWarehouse = await warehouseApi.getWarehouse(warehouseId);
      setWarehouse(foundWarehouse);
    } catch (error) {
      console.error("Error loading warehouse:", error);
    }
  };

  const loadCategories = async () => {
    try {
      const settings = await warehouseApi.getSettings();
      setCategories(settings.itemCategories || []);
      setMeasurementUnit(settings.measurementUnit || "in");
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  const loadItem = async () => {
    try {
      const foundItem = await warehouseApi.getInventoryItem(itemId);
      setItem(foundItem);

      form.setFieldsValue({
        itemId: foundItem.itemId,
        itemName: foundItem.name,
        packageTypeId: foundItem.packageTypeId,
        description: foundItem.description,
        location: foundItem.location,
        isUnique: !foundItem.isFungible,
        serialNumbersList: foundItem.serialNumbersList || [],
        quantity: foundItem.quantity,
        unitPrice: foundItem.unitPrice
      });
    } catch (error) {
      console.error("Error loading item:", error);
      message.error("Error loading item");
      history.goBack();
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const serialNumbersList = values.serialNumbersList || [];
      const quantity = values.quantity || 0;

      if (serialNumbersList.length > quantity) {
        message.error(
          `Serial numbers (${serialNumbersList.length}) cannot exceed quantity (${quantity})`
        );
        setLoading(false);
        return;
      }

      const serialNumbers = serialNumbersList.map((s) => s.serialNumber).filter(Boolean);
      const uniqueSerials = new Set(serialNumbers);
      if (uniqueSerials.size !== serialNumbers.length) {
        message.error("Duplicate serial numbers are not allowed");
        setLoading(false);
        return;
      }

      const itemData = {
        warehouseId,
        name: values.itemName,
        packageTypeId: values.packageTypeId,
        description: values.description,
        location: values.location,
        isFungible: !values.isUnique,
        serialNumbersList,
        quantity: serialNumbersList.length || values.quantity || 0,
        unitPrice: values.unitPrice || 0
      };

      await warehouseApi.updateInventoryItem(itemId, itemData);

      message.success("Item updated successfully");
      history.push(`/warehouses/${warehouseId}/item-details/${itemId}`);
    } catch (error) {
      console.error("Error updating item:", error);
      message.error("Failed to update item");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    history.push(`/warehouses/${warehouseId}/item-details/${itemId}`);
  };

  const [uploadedImagePath, setUploadedImagePath] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (file) => {
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("image", file);

      const response = await warehouseApi.uploadInventoryItemImage(itemId, formData);
      const imageUrl = response?.url;

      if (imageUrl) {
        message.success("Image uploaded successfully!");
        setUploadedImagePath(imageUrl);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      message.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
    return false;
  };

  if (!item) {
    return <div>Loading...</div>;
  }

  return (
    <div className="edit-inventory-item-page">
      <div className="breadcrumb-section">
        <div className="breadcrumbs">
          <span
            className="breadcrumb-item"
            onClick={() => history.push("/warehouses")}
            style={{ textDecoration: "none" }}
          >
            WAREHOUSES
          </span>
          <span
            className="breadcrumb-item"
            onClick={handleCancel}
            style={{ textDecoration: "none" }}
          >
            {warehouse?.name?.toUpperCase() || "INVENTORY"}
          </span>
          <span className="breadcrumb-item" style={{ textDecoration: "none" }}>
            EDIT ITEM
          </span>
        </div>
      </div>

      {/* Warehouse Header - consistent with inventory page */}
      <div className="warehouse-header">
        <div className="warehouse-info">
          <h3 className="warehouse-title">
            📦 {warehouse?.name} - Edit Item: {item.name}
          </h3>
          <Text className="warehouse-address">
            {warehouse?.address}, {warehouse?.city}, {warehouse?.state}{" "}
            {warehouse?.zipCode}
          </Text>
        </div>
      </div>

      {/* Action Header */}
      <div className="inventory-header">
        <div className="search-section">
          <Text className="action-text">Edit Item</Text>
        </div>
        <div className="action-buttons">
          <Button onClick={handleCancel} className="cancel-btn">
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
            className="add-item-btn"
          >
            {loading ? "Saving..." : "Update Item"}
          </Button>
        </div>
      </div>

      {/* Compact Form Container */}
      <div className="form-content">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="compact-form"
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="itemId" label="Item ID">
                <Input placeholder="Auto-generated" disabled />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="itemName"
                label="Item Name"
                rules={[{ required: true, message: "Please enter item name" }]}
              >
                <Input placeholder="Enter item name" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea placeholder="Enter item description" rows={3} />
          </Form.Item>

          <Form.Item name="packageTypeId" label="Package Type">
            <Select
              placeholder="Select package type"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {packageTypes.map((type) => {
                const displayLength =
                  measurementUnit === "in" && type.maxLength
                    ? (type.maxLength / 2.54).toFixed(1)
                    : type.maxLength;
                return (
                  <Option key={type.id} value={type.id}>
                    {type.name}
                    {(type.maxWeight || type.maxLength) && (
                      <span
                        style={{ color: "#8c8c8c", fontSize: "12px", marginLeft: "8px" }}
                      >
                        {type.maxWeight && `${type.maxWeight}kg`}
                        {type.maxWeight && type.maxLength && " · "}
                        {type.maxLength && `${displayLength}${measurementUnit}`}
                      </span>
                    )}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          <Form.Item label="Item Image">
            <Upload
              name="file"
              listType="picture"
              beforeUpload={handleImageUpload}
              maxCount={1}
              showUploadList={false}
            >
              <Button loading={uploadingImage}>
                {uploadingImage ? "Uploading..." : "Click to upload"}
              </Button>
            </Upload>
            {(uploadedImagePath || item.image) && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={uploadedImagePath || item.image}
                  alt="Item preview"
                  style={{ maxWidth: 200, maxHeight: 200, objectFit: "contain" }}
                />
              </div>
            )}
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="location"
                label="Location"
                rules={[{ required: true, message: "Please select location" }]}
              >
                <Select placeholder="Select location">
                  {locations.map((location) => (
                    <Option key={location.id} value={location.id}>
                      {location.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="isUnique" label="Item Type" valuePropName="checked">
                <Switch checkedChildren="Unique" unCheckedChildren="Non-Unique" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="quantity"
                label="Quantity"
                rules={[
                  {
                    validator: (_, value) => {
                      if (value === null || value === undefined || value === "") {
                        return Promise.resolve();
                      }
                      if (!Number.isInteger(value) || value < 0) {
                        return Promise.reject(
                          new Error("Quantity must be a positive integer")
                        );
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={0}
                  placeholder="0"
                  parser={(value) => value.replace(/[^0-9]/g, "")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unitPrice" label="Unit Price ($)">
                <InputNumber
                  style={{ width: "100%" }}
                  min={0}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <div
            style={{
              borderTop: "2px solid #d9d9d9",
              marginTop: 32,
              marginBottom: 24,
              paddingTop: 24
            }}
          >
            <Text strong style={{ fontSize: 16, display: "block", marginBottom: 16 }}>
              Serial Numbers - Attributes - PO
            </Text>
            <div style={{ marginBottom: 16 }}>
              <Space>
                <InputNumber
                  min={1}
                  max={100}
                  value={bulkAddCount}
                  onChange={setBulkAddCount}
                  style={{ width: 70 }}
                  placeholder="#"
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const currentList = form.getFieldValue("serialNumbersList") || [];
                    const newFields = Array(bulkAddCount).fill({});
                    form.setFieldsValue({
                      serialNumbersList: [...currentList, ...newFields]
                    });
                    setBulkAddCount(1);
                  }}
                >
                  Add New
                </Button>
              </Space>
            </div>
            <Form.List name="serialNumbersList">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <Row key={field.key} gutter={16} style={{ marginBottom: 8 }}>
                      <Col span={8}>
                        <Form.Item
                          {...field}
                          name={[field.name, "serialNumber"]}
                          fieldKey={[field.fieldKey, "serialNumber"]}
                          rules={[{ required: true, message: "Serial number required" }]}
                        >
                          <Input placeholder="Serial Number" />
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "itemAttribute"]}
                          fieldKey={[field.fieldKey, "itemAttribute"]}
                        >
                          <Select placeholder="Attribute" allowClear showSearch>
                            <Option key="none" value="None">
                              None
                            </Option>
                            {categories.map((cat) => (
                              <Option key={cat} value={cat}>
                                {cat}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={7}>
                        <Form.Item
                          {...field}
                          name={[field.name, "po"]}
                          fieldKey={[field.fieldKey, "po"]}
                        >
                          <Input placeholder="PO" />
                        </Form.Item>
                      </Col>
                      <Col span={2}>
                        <Popconfirm
                          title="Delete serial number?"
                          description="Are you sure you want to delete this serial number?"
                          onConfirm={() => remove(field.name)}
                          okText="Yes"
                          cancelText="No"
                        >
                          <Button type="text" danger icon={<MinusCircleOutlined />} />
                        </Popconfirm>
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </Form.List>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default EditInventoryItemPage;
